/**
 * The status badge's DESIGN TOKENS — the one place the domain SSOT's semantic
 * outputs meet this app's palette and message catalog.
 *
 * `listing_status_badge` / `listing_owner_badge` (domain-core, through the wasm
 * rail) answer with a `ListingStatusBadge`: a `kind` (the TONE — is this good
 * news, a warning, something the owner must act on) and a `label` (WHICH WORD).
 * Neither is a colour and neither is a sentence; turning them into one is this
 * module's whole job, and both maps are `satisfies Record<…>` over the SSOT's
 * own enums, so a new kind or label variant is a compile error naming it rather
 * than a badge that renders untoned or unworded.
 *
 * ★ WHY IT IS NOT INSIDE `ListingCard.tsx`, where both maps used to live. The
 * publish-HOLD notice on the editor overview tones itself from the very same
 * decision (`listingOwnerBadgeForNames(...).kind`), and a second copy of a tone
 * table is a drift that nothing would have caught — the two surfaces would
 * simply have disagreed about what "Needs attention" looks like. One table, two
 * readers.
 *
 * It is a `.ts` in the entity layer rather than a `lib/` module because a
 * Tailwind class list is presentation, and `MessageKeys` is the app's own
 * catalog type; nothing here imports React.
 */
import type { MessageKeys } from '@/shared/i18n/messages_gen';
import { ListingStatusKind, ListingStatusLabel } from '@letsgethome/lib/wasm';

/**
 * The SSOT's semantic TONE → the swatch that paints it. Used for the card
 * badge's dot and for the publish-hold notice's accent, so the two cannot say
 * the same state in different colours.
 */
export const KIND_CLASS = {
  [ListingStatusKind.Positive]: 'bg-success-text',
  [ListingStatusKind.Neutral]: 'bg-muted-foreground',
  [ListingStatusKind.Warning]: 'bg-warning-text',
  [ListingStatusKind.Info]: 'bg-info-text',
  [ListingStatusKind.Destructive]: 'bg-destructive',
} satisfies Record<ListingStatusKind, string>;

/**
 * The SSOT's label variant → the catalog key that words it.
 *
 * TOTAL over `ListingStatusLabel`, which is what stopped the hand-rolled twin
 * maps this replaced from drifting again: they had disagreed with the SSOT on
 * PUBLISHING and mislabeled DELETED as "In progress".
 */
export const LABEL_KEY = {
  [ListingStatusLabel.InProgress]: 'statusInProgress',
  [ListingStatusLabel.AwaitingSignature]: 'statusAwaitingSignature',
  [ListingStatusLabel.Listed]: 'statusListed',
  [ListingStatusLabel.Unlisted]: 'statusUnlisted',
  [ListingStatusLabel.Publishing]: 'statusPublishing',
  [ListingStatusLabel.AddressUnderReview]: 'statusAddressUnderReview',
  [ListingStatusLabel.NeedsAttention]: 'statusNeedsAttention',
  [ListingStatusLabel.Deleted]: 'statusDeleted',
  [ListingStatusLabel.Suspended]: 'statusSuspended',
} as const satisfies Record<ListingStatusLabel, MessageKeys<'listings.card'>>;
